<template>
  <label
    class="checkbox"
    :class="[
      `checkbox--align-${align}`,
      {
        'checkbox--semi-selected': semiSelected && value,
        'checkbox--disabled': disabled,
        'checkbox--checked': value,
        'checkbox--radio': type === 'radio',
      }, `checkbox--surface-${surface}`
    ]"
  >
    <input
      :id="id"
      :name="name"
      :value="value"
      :checked="value"
      :disabled="disabled"
      type="checkbox"
      @change="updateValue($event.target.checked)"
    >
    <span class="checkbox__box">
      <template v-if="value">
        <span
          v-if="type === 'checkbox'"
          class="checkbox__check-icon"
          v-html="decodeSvgDataUrl(semiSelected ? minusIcon : checkIcon)"
        />
        <span
          v-else-if="type === 'radio'"
          class="checkbox__dot-icon"
        />
      </template>
    </span>

    <div v-if="description" class="checkbox__label--wrapper">
      <span class="checkbox__label">
        <slot>{{ label }}</slot>
      </span>

      <span class="checkbox__description">
        <slot>{{ description }}</slot>
      </span>
    </div>

    <span v-else-if="label || $slots.default" class="checkbox__label">
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<script>
import SurfaceHelper from 'mixins/surface_helper';
import decodeSvgDataUrl from '../../../../utils/decodeSvgDataUrl';

const checkIcon = require('assets/images/base-icons/checked.svg');
const minusIcon = require('assets/images/base-icons/minus.svg');

export default {
  mixins: [SurfaceHelper],
  props: {
    value: {
      type: Boolean,
      required: false,
      default: false,
    },
    type: {
      type: String,
      default: 'checkbox',
      validator: value => ['checkbox', 'radio'].includes(value),
    },
    name: {
      type: String,
      required: false,
      default: '',
    },
    id: {
      type: String,
      required: false,
      default: '',
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // semi selected works correctly only if value == true
    semiSelected: {
      type: Boolean,
      default: false,
    },
    description: {
      type: String,
      required: false,
      default: null,
    },
    align: {
      type: String,
      default: 'center',
      validator: value => ['top', 'bottom', 'center'].includes(value),
    },
  },
  data() {
    return {
      checkIcon,
      minusIcon,
    };
  },
  methods: {
    decodeSvgDataUrl,
    updateValue(value) {
      this.$emit('input', value);
    },
  },
};
</script>

<style lang="scss" scoped>
.checkbox {
  @apply flex flex-row cursor-pointer rounded-md;

  --text-color: theme('colors.neutral.900');
  --bg-color: theme('colors.neutral.100');
  --border-color: theme('colors.neutral.400');
  --icon-color: theme('colors.neutral.100');
  --rounded: theme('borderRadius.sm');
  --outline: theme('colors.info.1000');

  input[type="checkbox"] {
    @apply absolute cursor-pointer h-0 w-0;

    &:focus-visible + .checkbox__box {
      @apply outline outline-2 outline-offset-2 outline-[var(--outline)];
    }
  }

  &:hover {
    --bg-color: theme('colors.warning.100');
  }

  &--disabled {
    @apply cursor-not-allowed;
  }

  &--align-top {
    @apply items-start;

    .checkbox__box {
      @apply mt-[-3px];
    }
  }

  &--align-bottom {
    @apply items-end;
  }

  &--align-center {
    @apply items-center;
  }

  &--center-vertical {
    @apply items-center;

    .checkbox__label {
      @apply mt-0;
    }
  }

  &--search-bar {
    @apply pt-3 w-fit;
  }

  &--surface-accent {
    --text-color: theme('colors.neutral.100');
    --outline: theme('colors.warning.500');

    &:not(.checkbox--radio) {
      --icon-color: theme('colors.info.1000');
    }
  }

  &:focus-visible {
    @apply outline outline-2 outline-offset-2 outline-[var(--outline)];
  }

  &__label--wrapper {
    @apply flex items-start flex-col gap-[1px] overflow-hidden min-w-0 flex-1;
  }

  &__label {
    @apply ml-2 inline-block grow text-[var(--text-color)] leading-[20px] overflow-hidden min-w-0 w-full;
  }

  &__description {
    @apply ml-2 inline-block grow mt-1 text-neutral-600 overflow-hidden text-sm font-normal;
  }

  &__box {
    @apply content-[''] flex items-center justify-center h-5 w-5 shrink-0 rounded-[var(--rounded)] border-2 border-solid
    bg-[var(--bg-color)] border-[var(--border-color)];
  }

  &__check-icon {
    @apply w-4 h-4 flex;

    ::v-deep svg {
      @apply fill-[var(--icon-color)] w-4 h-4;
    }
  }

  &__dot-icon {
    @apply w-2 h-2 flex bg-[var(--icon-color)] rounded-full;
  }

  &.checkbox--radio {
    --rounded: 100%;
  }

  &.checkbox--checked {
    --bg-color: theme('colors.success.600');
    --border-color: theme('colors.success.600');

    &:hover:not(.checkbox--disabled) {
      --bg-color: theme('colors.success.700');
      --border-color: theme('colors.success.700');
    }

    .checkbox__label {
      @apply font-medium;
    }
  }

  &.checkbox--semi-selected.checkbox--checked {
    --bg-color: theme('colors.info.200');
    --border-color: theme('colors.info.400');
    --icon-color: theme('colors.info.400');

    &:hover:not(.checkbox--disabled) {
      --bg-color: theme('colors.info.300');
      --border-color: theme('colors.info.500');
      --icon-color: theme('colors.info.500');
    }

    .checkbox__label {
      @apply font-normal;
    }
  }

  &.checkbox--semi-selected.checkbox--checked.checkbox--disabled {
    @apply opacity-30;
  }

  &.checkbox--checked.checkbox--disabled {
    @apply opacity-30;
  }
}
</style>
